import React from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAdminAccountQuery } from '../../api/admin';
import { AppPageHeader } from '../../components';
import {
	AccountHistoryPanel,
	AccountInfoCard,
	AccountStatTiles,
	AccountStatusChip,
	OrganizationMembersTable,
	OverviewPanel,
	TaskTrailPanel,
	TeamsTable,
} from '../../components/Admin';

/** Gap between boxes, as on the user Overview tab. */
const GAP = 2.5;
const TABLE_HEIGHT = 400;

/**
 * One account (commercial relationship): the numbers a sales person checks first,
 * the change log, the editable Account Info card, and the covered teams and their users.
 */
export default function AccountDetailPage() {
	const { account_id } = useParams<{ account_id: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	const accountId = parseInt(account_id!, 10);

	// Router state gives an instant title when navigating within the app; the query is the source of truth.
	const { accountName: stateName } = (location.state as { accountName?: string }) ?? {};
	const { data: account, isLoading, isError } = useAdminAccountQuery(accountId);
	const title = account?.name ?? stateName ?? `Account #${accountId}`;

	return (
		<Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
			<AppPageHeader>
				<Stack direction="row" alignItems="center" spacing={2} mb={1}>
					<Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/users?tab=accounts')}>
						All Accounts
					</Button>
				</Stack>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<Typography variant="h5">{title}</Typography>
					{account && <AccountStatusChip account={account} />}
				</Stack>
				{account && (
					<Typography variant="body2" color="text.secondary">
						Organization:{' '}
						<Link
							component={RouterLink}
							to={`/admin/organizations/${account.organization_id}`}
							state={{ organizationName: account.organization_name }}
						>
							{account.organization_name}
						</Link>
						{' | '}
						Teams:{' '}
						{account.teams.length > 0
							? account.teams.map((t, i) => (
								  <React.Fragment key={t.id}>
									  {i > 0 && ', '}
									  <Link
										  component={RouterLink}
										  to={`/admin/teams/${t.id}`}
										  state={{ teamName: t.name, organizationName: account.organization_name }}
									  >
										  {t.name}
									  </Link>
								  </React.Fragment>
							  ))
							: null}
					</Typography>
				)}
			</AppPageHeader>
			{/* Same content box as the user page: page margins, scrolls on its own. mt: 0 on the first
			    grid stops MUI's negative container margin from riding up over the header links. */}
			<Box sx={{ mx: '40px', mt: '20px', mb: '20px', flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>

				{isError && (
					<Alert severity="error" sx={{ mb: GAP }}>
						Account #{accountId} could not be loaded.
					</Alert>
				)}

				<Grid container spacing={GAP} sx={{ mt: 0 }}>
					<Grid item xs={12} md={8}>
						{/* Tiles on top, Tasks filling whatever height the right column sets, scrolling inside. */}
						<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
							<AccountStatTiles accountId={accountId} account={account} loading={isLoading} />
							<Box sx={{ flex: 1, minHeight: 0 }}>
								<TaskTrailPanel subject={{ accountId }} subjectName={account?.name} fill />
							</Box>
						</Box>
					</Grid>
					<Grid item xs={12} md={4}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP }}>
							<AccountInfoCard account={account} loading={isLoading} />
							<AccountHistoryPanel accountId={accountId} />
						</Box>
					</Grid>
				</Grid>

				<Grid container spacing={GAP} sx={{ mt: 0 }}>
					<Grid item xs={12} md={6}>
						<OverviewPanel title="Teams" caption="covered by this account">
							{account && (
								<TeamsTable
									organizationId={account.organization_id}
									teamIds={account.teams.map((t) => t.id)}
									height={TABLE_HEIGHT}
								/>
							)}
						</OverviewPanel>
					</Grid>
					<Grid item xs={12} md={6}>
						<OverviewPanel title="Users" caption="members of the covered teams">
							{account && (
								<OrganizationMembersTable
									organizationId={account.organization_id}
									teamNames={account.teams.map((t) => t.name)}
									height={TABLE_HEIGHT}
								/>
							)}
						</OverviewPanel>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
}
